import { ReactNode, createContext, useContext, useEffect, useRef } from 'react'

import { StoreApi, createStore, useStore } from 'zustand'

import { callApiNew } from 'api/axios'
import { formatArticle } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { Article, ExpertUser, ExpertUserSummary } from 'interfaces/types'
import { Pagination, WithPagination } from 'interfaces/types/Utilities'

import { ExpertStoreType, ExpertStoreValueType } from './types'

const MyContext = createContext<StoreApi<ExpertStoreType>>(null as any)
export const fetchRelatedPosts = async ({
  id,
  type = 0,
  page = 1,
  numberOfPost,
  offset = 0,
}: {
  id: string
  type: 0 | 1
  page?: number
  numberOfPost?: number
  offset?: number
}): Promise<WithPagination<{ articles: Array<Article> }>> => {
  try {
    const response = await callApiNew(PATHS.EXPERT.GET_EXPERT_POST, {
      params: {
        reviewedByExpert: type,
        numberOfPost,
        userId: id,
        page,
        offset,
      },
    })
    return {
      articles: (response?.data?.articles || []).map(formatArticle),
      pagination: response?.data?.paging,
    }
  } catch (error) {
    return { articles: [], pagination: null }
  } finally {
  }
}
const createExpertStore = (init?: ExpertStoreValueType) =>
  createStore<ExpertStoreType>((set, get) => ({
    ...(init || ({} as any)),
    setExpertList(
      expert: WithPagination<{ experts: Array<ExpertUserSummary> }>,
    ) {
      set({
        expertList: expert,
      })
    },
    async setCurrentExpert(expert: ExpertUser) {
      set({
        currentExpert: expert,
      })
    },
    setCurrentExpertRelativePosts(articles: Array<Article>) {
      set((state) => ({
        currentExpertRelativePosts: [
          ...(state.currentExpertRelativePosts || []),
          ...articles,
        ],
      }))
    },
    setCurrentExpertMedicalReviewed(articles: Array<Article>) {
      set({
        currentExpertMedicalReviewed: [
          ...(get().currentExpertMedicalReviewed || []),
          ...articles,
        ],
      })
    },
    setCurrentExpertRelativePostsPagination(pagination: Pagination) {
      set({
        currentExpertRelativePostsPagination: pagination,
      })
    },
    setCurrentExpertMedicalReviewedPagination(pagination: Pagination) {
      set({
        currentExpertMedicalReviewedPagination: pagination,
      })
    },
  }))

export const ExpertStoreProvider = ({
  init,
  refreshKey,
  children,
}: {
  init?: Partial<ExpertStoreType>
  refreshKey?: string
  children?: ReactNode
}) => {
  const storeRef = useRef(createExpertStore(init as ExpertStoreType))
  const store = storeRef.current // or any better solution

  //  handle client side routing, reset new data from props
  useEffect(() => {
    refreshKey && storeRef.current.setState(init as ExpertStoreType)
  }, [refreshKey])

  return <MyContext.Provider value={store}>{children}</MyContext.Provider>
}

// wrapper for zustand useStore function
export const useExpertStore = (
  selector: (state: ExpertStoreType) => any,
  equalityFn?: (a: unknown, b: unknown) => boolean,
) => {
  const store = useContext(MyContext)
  const slice = useStore(store, selector, equalityFn)

  return slice as ExpertStoreType
}
